import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle2, Images, HelpCircle } from 'lucide-react';

// Symptom images, supplied by client. Fill each slug with a URL (or /public path)
// when the images arrive; empty values fall back to a text-only card automatically.
const IMG = {
  control_blank: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/657c135e6fa8994084685e26efe80b8d5c6c77f45c71c59f9d586b2d1cb950fb.jpeg',
  control_frozen: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/72a5a688ba6689e30df3b5df8a8715769e344785b9d5c2f1f0d2006354c72f6e.jpeg',
  control_error: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/625431ec16e0d63334541470c4212313157574ca8803d35458528ecb2fde9ac9.jpeg',
  door_frost: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/f809de6b7e237e3339a8e97191ef2174e79d96d974e793e409f9a9d42221e693.jpeg',
  gasket_torn: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/5a396192df18776e3f497c80ea467eee43fe2885a20fb0e2c07f496e4edf93e7.jpeg',
  chamber_ice: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/80d527b30ee330f3961a3e7fcb18271a10d6db2249c0915ba69f2a8e1be0fb0f.jpeg',
  condenser_frost: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/1cbabd27d75342a1ced55e55051d12f1433a0cb1925e2faee85807bb04b695c8.jpeg',
  pump_oil: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/6f26a38ae66fcef358ff7f9a38c2a7cebc88369d68f655096513e843ce7be172.jpeg',
  hose_crack: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/e62b4ffd23eb27643bd2fd4a74808ccd01c560f3b10295cf3b887879ec4c0b56.jpeg',
  drain_leak: 'https://static.prod-images.emergentagent.com/jobs/9e1fb140-210f-412f-9430-9687aa3be1b0/images/12751a6123bf8efb69e753d98cebb6da6fa2c645149badbdcf62a162ed17e3ba.jpeg',
};

// Slides per part. `issue` matches a chip in InteractiveDiagnostic PARTS so selections stay in sync.
export const HELP = {
  control: [
    { img: IMG.control_blank, title: 'Blank or dead screen', notice: 'The display is completely dark, no text, no lights.', meaning: 'Often a power supply, blown fuse or a loose display cable.', issue: 'Blank / frozen display' },
    { img: IMG.control_frozen, title: 'Frozen / stalled display', notice: 'The screen is stuck on one step and won’t move on.', meaning: 'Usually a controller lock-up or a failing temperature sensor.', issue: 'Blank / frozen display' },
    { img: IMG.control_error, title: 'Error code showing', notice: 'A code or red warning is on the screen.', meaning: 'Points to a specific fault, jot the code down for us.', issue: 'Error code showing' },
    { title: 'Won’t power on at all', notice: 'Nothing happens when you switch it on.', meaning: 'Power inlet, switch or main board, we’ll trace it.', issue: "Won't power on" },
  ],
  door: [
    { img: IMG.door_frost, title: 'Frost or fog on the glass', notice: 'Condensation or ice forms on the door window.', meaning: 'Can signal a seal, gasket or vacuum problem.', issue: 'Frost or fog on glass' },
    { title: 'Won’t hold vacuum / hissing', notice: 'You hear air hissing or the vacuum won’t build.', meaning: 'Door alignment, latch or worn gasket.', issue: "Won't seal / losing vacuum" },
    { title: 'Handle or latch broken', notice: 'The handle is loose, stuck or snapped.', meaning: 'Latch mechanism needs a rebuild.', issue: 'Handle or latch broken' },
  ],
  gasket: [
    { img: IMG.gasket_torn, title: 'Cracked or torn seal', notice: 'You can see splits or tears in the rubber.', meaning: 'The gasket has aged out and needs replacing.', issue: 'Cracked / torn gasket' },
    { title: 'Gasket loose or falling out', notice: 'The rubber seal sits proud or pops out of its groove.', meaning: 'Seal seat or gasket size issue.', issue: 'Gasket loose or falling out' },
    { title: 'Frost forming at the seal', notice: 'Ice builds up right where the door meets the body.', meaning: 'Air is sneaking past the seal.', issue: 'Frost forming at the seal' },
  ],
  chamber: [
    { img: IMG.chamber_ice, title: 'Heavy ice buildup', notice: 'Thick frost coats the trays and chamber walls.', meaning: 'Vacuum or refrigeration isn’t balanced.', issue: 'Ice buildup' },
    { title: 'Product still wet after a cycle', notice: 'Food comes out damp or spongy.', meaning: 'Cycle, vacuum or heating fault.', issue: 'Product still wet' },
    { title: 'Not freezing', notice: 'The chamber never gets properly cold.', meaning: 'Refrigeration or compressor issue.', issue: 'Not freezing' },
  ],
  cooling: [
    { img: IMG.condenser_frost, title: 'Not getting cold', notice: 'The machine runs but never chills down.', meaning: 'Refrigeration charge or compressor.', issue: 'Not getting cold' },
    { title: 'Compressor silent / not running', notice: 'No hum or vibration from the cooling unit.', meaning: 'Compressor, relay or wiring.', issue: 'Compressor not running' },
    { title: 'Overheating', notice: 'The unit gets very hot or shuts down mid-cycle.', meaning: 'Airflow, fan or refrigeration fault.', issue: 'Overheating' },
  ],
  condenser: [
    { img: IMG.condenser_frost, title: 'Coils caked in frost', notice: 'The coils are buried under thick ice.', meaning: 'Defrost, airflow or refrigerant issue.', issue: 'Frost / ice on coils' },
    { title: 'Dirty or blocked coils', notice: 'Coils are clogged with dust or debris.', meaning: 'Restricted airflow hurting performance.', issue: 'Dirty / blocked coils' },
  ],
  drain: [
    { img: IMG.drain_leak, title: 'Water leaking from the drain', notice: 'Water pools or drips at the front-bottom.', meaning: 'Worn valve or seal.', issue: 'Leaking water' },
    { title: 'Won’t drain', notice: 'Melt water stays inside after a cycle.', meaning: 'Blocked or stuck drain valve.', issue: "Won't drain" },
  ],
  hose: [
    { img: IMG.hose_crack, title: 'Cracked or split hose', notice: 'The vacuum hose has visible cracks or a tear.', meaning: 'Air leak killing your vacuum.', issue: 'Cracked hose' },
    { title: 'Loose fitting / air leak', notice: 'A connection hisses or feels loose.', meaning: 'Fitting or clamp needs attention.', issue: 'Loose fitting / air leak' },
  ],
  pump: [
    { img: IMG.pump_oil, title: 'Oil leaking from the pump', notice: 'An oil puddle forms under the pump.', meaning: 'Seals or gaskets are worn.', issue: 'Oil leaking' },
    { title: 'Loud knocking or grinding', notice: 'The pump is much noisier than usual.', meaning: 'Internal wear, often rebuildable.', issue: 'Loud / knocking noise' },
    { title: 'Won’t pull vacuum', notice: 'The gauge never reaches proper vacuum.', meaning: 'Pump service or an upstream air leak.', issue: 'Not pulling vacuum' },
  ],
  casters: [
    { title: 'Broken or seized wheel', notice: 'A caster is cracked, jammed or missing.', meaning: 'Simple caster replacement.', issue: 'Wheel broken' },
    { title: 'Rust or frame damage', notice: 'Visible rust or a bent/dented frame.', meaning: 'Frame rebuild or refinishing.', issue: 'Rust / frame damage' },
  ],
};

export const SymptomHelper = ({ partId, partLabel, selectedIssues = [], onToggle, onClose }) => {
  const slides = HELP[partId] || [];
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setI((v) => (v + 1) % slides.length), [slides.length]);
  const prev = () => setI((v) => (v - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const t = setInterval(next, 4500);
    return () => clearInterval(t);
  }, [paused, next, slides.length]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') next(); if (e.key === 'ArrowLeft') prev(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!slides.length) return null;
  const s = slides[i];
  const picked = selectedIssues.includes(s.issue);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" data-testid="symptom-helper" onClick={onClose}>
      <div
        className="relative w-full max-w-lg bg-[#023F63] border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <span className="flex items-center gap-2 text-white font-bold"><Images className="h-4 w-4 text-[#CE8946]" /> {partLabel}, common symptoms</span>
          <button onClick={onClose} className="text-white/60 hover:text-white" aria-label="Close" data-testid="symptom-helper-close"><X className="h-5 w-5" /></button>
        </div>

        {/* Media */}
        <div className="relative aspect-[16/10] bg-[#002B47]">
          {s.img ? (
            <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
              <HelpCircle className="h-12 w-12 mb-2" />
              <span className="text-xs">No photo, description below</span>
            </div>
          )}
          {slides.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center" aria-label="Previous" data-testid="symptom-prev"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center" aria-label="Next" data-testid="symptom-next"><ChevronRight className="h-5 w-5" /></button>
            </>
          )}
        </div>

        {/* Caption */}
        <div className="p-5">
          <h4 className="text-lg font-bold text-white">{s.title}</h4>
          <p className="text-sm text-white/70 mt-1"><span className="text-white/50">You might notice:</span> {s.notice}</p>
          <p className="text-sm text-white/70 mt-1"><span className="text-white/50">What it might mean:</span> {s.meaning}</p>

          <button
            onClick={() => onToggle(s.issue)}
            className={`w-full mt-4 inline-flex items-center justify-center gap-2 font-bold px-5 py-3 rounded-md transition-colors ${picked ? 'bg-[#CE8946] text-[#003456]' : 'border border-[#CE8946]/50 text-[#CE8946] hover:bg-[#CE8946]/10'}`}
            data-testid="symptom-select"
          >
            <CheckCircle2 className="h-4 w-4" /> {picked ? "Added, this is happening" : "Yes, this is what's happening"}
          </button>

          {/* Dots */}
          {slides.length > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {slides.map((_, idx) => (
                <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-6 bg-[#CE8946]' : 'w-1.5 bg-white/25'}`} />
              ))}
            </div>
          )}
          <p className="text-center text-[11px] text-white/40 mt-3">Pick everything that matches, then close to add any more.</p>
        </div>
      </div>
    </div>
  );
};

export default SymptomHelper;
